import { useEffect, useState } from 'react'
import { Download, Tv, Settings, CheckCircle, Smartphone, Monitor } from 'lucide-react'

interface LatestRelease {
  version: string
  filename: string
  size: string
  updated: string
  downloadUrl: string
}

export default function InstallGuide() {
  const [release, setRelease] = useState<LatestRelease | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('/api/downloads/latest')
      .then(res => res.json())
      .then(data => {
        if (!data.error) {
          setRelease(data)
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Tv className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">OBACast</h1>
              <p className="text-slate-400 text-sm">Android TV App</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">
            Install OBACast on Your Fire Stick
          </h2>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Stream live TV on your Amazon Fire Stick or Android TV device in just a few simple steps.
          </p>
        </div>

        {/* Download Card */}
        {release && (
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 mb-12 text-center">
            <h3 className="text-2xl font-bold text-white mb-2">Latest Version: {release.version}</h3>
            <p className="text-blue-100 mb-6">Released {release.updated} | {release.size}</p>
            <a
              href={release.downloadUrl}
              className="inline-flex items-center gap-2 bg-white text-blue-600 font-semibold px-8 py-4 rounded-xl hover:bg-blue-50 transition-colors text-lg"
            >
              <Download className="w-6 h-6" />
              Download APK
            </a>
            <p className="text-blue-100 text-sm mt-4">
              Direct link: <code className="bg-blue-700/50 px-2 py-1 rounded">obacast.com/install</code>
            </p>
          </div>
        )}

        {loading && (
          <div className="bg-slate-800 rounded-2xl p-8 mb-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="text-slate-400 mt-4">Loading release info...</p>
          </div>
        )}

        {/* Installation Steps */}
        <div className="space-y-8">
          <h3 className="text-2xl font-bold text-white flex items-center gap-3">
            <Settings className="w-7 h-7 text-blue-400" />
            Installation Guide
          </h3>

          {/* Step 1 */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">1</span>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-white mb-2">Install the Downloader App</h4>
                <p className="text-slate-300 mb-4">
                  On your Fire Stick, go to the Amazon App Store and search for <strong>"Downloader"</strong> by AFTVnews.
                  Install it - it's free!
                </p>
                <div className="bg-slate-900/50 rounded-lg p-4 text-sm text-slate-400">
                  <strong>Tip:</strong> You can also use voice search - just say "Downloader app"
                </div>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">2</span>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-white mb-2">Enable Apps from Unknown Sources</h4>
                <p className="text-slate-300 mb-4">
                  Go to <strong>Settings → My Fire TV → Developer Options</strong> and enable:
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Apps from Unknown Sources → ON
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Install unknown apps → Downloader → ON
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">3</span>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-white mb-2">Download OBACast</h4>
                <p className="text-slate-300 mb-4">
                  Open the Downloader app and enter this URL:
                </p>
                <div className="bg-slate-900 rounded-lg p-4 flex items-center justify-between">
                  <code className="text-2xl text-blue-400 font-mono">obacast.com/install</code>
                </div>
                <p className="text-slate-400 text-sm mt-3">
                  The download will start automatically. When prompted, click <strong>Install</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="text-xl font-semibold text-white mb-2">You're All Set!</h4>
                <p className="text-slate-300 mb-4">
                  Open OBACast from your apps. Log in with your credentials and enjoy live TV!
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="bg-green-600/20 text-green-400 px-3 py-1 rounded-full text-sm">
                    Live TV Streaming
                  </span>
                  <span className="bg-blue-600/20 text-blue-400 px-3 py-1 rounded-full text-sm">
                    EPG Guide
                  </span>
                  <span className="bg-purple-600/20 text-purple-400 px-3 py-1 rounded-full text-sm">
                    Auto Updates
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Device Compatibility */}
        <div className="mt-12 bg-slate-800/30 rounded-xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-blue-400" />
            Compatible Devices
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="bg-slate-800 rounded-lg p-4">
              <Monitor className="w-8 h-8 text-orange-400 mx-auto mb-2" />
              <p className="text-white font-medium">Fire TV Stick</p>
              <p className="text-slate-400 text-sm">All versions</p>
            </div>
            <div className="bg-slate-800 rounded-lg p-4">
              <Monitor className="w-8 h-8 text-orange-400 mx-auto mb-2" />
              <p className="text-white font-medium">Fire TV Cube</p>
              <p className="text-slate-400 text-sm">All versions</p>
            </div>
            <div className="bg-slate-800 rounded-lg p-4">
              <Tv className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-white font-medium">Android TV</p>
              <p className="text-slate-400 text-sm">Android 7+</p>
            </div>
            <div className="bg-slate-800 rounded-lg p-4">
              <Tv className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-white font-medium">Google TV</p>
              <p className="text-slate-400 text-sm">Chromecast</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 text-center text-slate-500 text-sm">
          <p>OBACast &copy; {new Date().getFullYear()}</p>
        </footer>
      </main>
    </div>
  )
}
